import { memo, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { handleError } from 'services/ErrorService/ErrorService';
import { fileBookIssues } from 'api/tickets/chegg';
import { withConfirmation } from 'components/DecisionTree/DTConfirmationView/withConfirmation';
import DTCopyTextSendMacroView from 'components/DecisionTree/NodeGroups/DTCopyTextSendMacro/DTCopyTextSendMacroView';
import CustomTextarea from 'components/common/CustomTextarea/CustomTextarea';
import { RETURN_FROM_CUSTOMER, USED_OTHER_DELIVERY_WITH_DETAILS } from './DTHandleCheggBookIssuesMap';
import { addNoteToSelectedTicket } from 'actions';
import {
  Container,
  DetailsContainer,
  DetailsTitle,
  InputContainer,
  InputSection,
  LeftSection,
  RightSection,
  InputText,
} from './DTHandleCheggBookIssuesNode.styles';
import {
  getSelectedOrderFromOrdersSearch,
  getSelectedItemsFromOrdersSearch,
  getSelectedCheggBookIssuesOrder,
  getSimplrAccountIdOfSelectedTicket,
  getDecisionNodeAtPath,
} from 'extractFromState';
import { buildBookIssuesOrderPayload } from './handleCheggBookIssuesNodeUtil';

const DTHandleCheggBookIssuesNode = () => {
  const dispatch = useDispatch();
  const simplrAccountId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const selectedOrder = useSelector(getSelectedOrderFromOrdersSearch);
  const selectedItems = useSelector(getSelectedItemsFromOrdersSearch);
  const selectedBookIssuesOrders = useSelector(getSelectedCheggBookIssuesOrder);
  const currentDecisionNode = useSelector(getDecisionNodeAtPath);

  const cheggBookIssues = currentDecisionNode.cheggBookIssues;
  const { issueType, approach, deliveryType } = cheggBookIssues;

  const [additionalDetails, setAdditionalDetails] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const shouldDisableActionButton = useMemo(() => {
    let actionButtonStatus = false;
    if (RETURN_FROM_CUSTOMER === deliveryType.value) {
      if (USED_OTHER_DELIVERY_WITH_DETAILS === approach.value) {
        actionButtonStatus = isEmpty(trackingCode) && isEmpty(carrierName);
      }
      actionButtonStatus = actionButtonStatus || isEmpty(additionalDetails);
    }

    return actionButtonStatus;
  }, [additionalDetails, trackingCode, carrierName, approach.value, deliveryType.value]);

  const createBookIssuesNote = () => {
    let bookIssuesNote = 'Book Issues Filed:';

    return `${bookIssuesNote} ${issueType.label} - ${approach.label}`;
  };

  const createAdditionalDeliveryIssueDetails = () => {
    if (RETURN_FROM_CUSTOMER === deliveryType.value) {
      const additionalDetailsElement = (
        <>
          {USED_OTHER_DELIVERY_WITH_DETAILS === approach.value && (
            <>
              <InputSection>
                <LeftSection>Tracking Code:</LeftSection>
                <RightSection data-testid="trackingCode">
                  <InputText type="text" className="trackingCode" onChange={(e) => setTrackingCode(e.target.value)} />
                </RightSection>
              </InputSection>
              <InputSection>
                <LeftSection>Carrier Name:</LeftSection>
                <RightSection data-testid="carrierName">
                  <InputText type="text" className="carrierName" onChange={(e) => setCarrierName(e.target.value)} />
                </RightSection>
              </InputSection>
            </>
          )}
        </>
      );

      return (
        <Container>
          <DetailsContainer>
            <DetailsTitle>Please fill out the details below to continue processing the book issue.</DetailsTitle>
            <InputContainer>
              {additionalDetailsElement}
              <InputSection>
                <LeftSection>Additional Details:</LeftSection>
                <RightSection data-testid="additionalDetails">
                  <CustomTextarea
                    placeholder="Please enter additional customer details"
                    onValueChange={setAdditionalDetails}
                  />
                </RightSection>
              </InputSection>
            </InputContainer>
          </DetailsContainer>
        </Container>
      );
    }
  };

  const handleNextButtonClick = async () => {
    try {
      const bookIssuesOrder = buildBookIssuesOrderPayload({
        additionalDetails,
        approach: approach?.value,
        carrierName,
        deliveryType: deliveryType?.value,
        issueType: issueType?.value,
        selectedBookIssuesOrders,
        selectedOrderItems: selectedOrder?.orderItems,
        selectedItems,
        trackingCode,
      });
      await fileBookIssues(simplrAccountId, selectedOrder.customerEmail, bookIssuesOrder);
      dispatch(addNoteToSelectedTicket(createBookIssuesNote()));
    } catch (error) {
      handleError({ error, showPopup: true });
    }
  };

  return (
    <>
      <DTCopyTextSendMacroView
        buttonText={`PROCESS BOOK ISSUES`}
        onNextButtonClick={handleNextButtonClick}
        hideCopyText={true}
        disableActionButton={shouldDisableActionButton}
        popupConfirmationText={'You are about to process this book issue'}
        customErrorMessage={
          'There was an error when attempting to process the book issue. Please try again in 5 mintues or escalate with the comment "Cannot process book issue."'
        }
      >
        {createAdditionalDeliveryIssueDetails()}
      </DTCopyTextSendMacroView>
    </>
  );
};

export default memo(withConfirmation(DTHandleCheggBookIssuesNode));
